import { useEffect, useMemo, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  TextField,
  Table,
  TableMetadata,
  Typography,
  useTable,
  AutocompleteMultipleSelect,
  Label,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Container, Alert, Modal as BSModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DatePickerRange, CommitteeDateRangePicker } from "./components";
import { DEFAULT_INSTITUTION_COMMITTEE } from "./constants";
import {
  canEmployeeBeRemoved,
  getCommitteeEmployeeDisplayName,
  setupCommitteeEmployees,
  updateCommitteeEmployee,
} from "./utils";
import {
  getInstitutionCommitteeById,
  searchInstitutionCommitteeEmployee,
  getInstitutionCommitteeEmployeeOib,
  saveInstitutionCommittee,
} from "../../api/Committee/InstitutionCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  FormCard,
  ToolbarEdit,
} from "../../components/";
import { OIB_REGEX } from "../../constants";
import { useEnumCodeBooks } from "../../hooks";
import { ValidationFailure } from "../../models";
import {
  OibEmployee,
  CommitteeEmployee,
  CommitteeTypeEnum,
  InstitutionCommittee,
  CommitteeRoleEnum,
} from "../../models/Committee";
import { CodeBookPagedFilter, SaveResult } from "../../models/Shared";
import { committeeApprovalDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";

export function CommitteeApprovalEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [item, setItem] = useState<InstitutionCommittee>(
    DEFAULT_INSTITUTION_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole.name}</div>;
        },
      },
      {
        headerClassName: "col-6",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "edit",
        header: t("common.edit"),
        accessor: (employee) => {
          return (
            <div className={"text-right"}>
              {employee.committeeRole.committeeType !==
                CommitteeTypeEnum.Institution && (
                <Button
                  size="sm"
                  outline={true}
                  onClick={() => handleDetailsClicked(employee)}
                >
                  {t("common.edit")}
                </Button>
              )}
            </div>
          );
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.committeeRole.id}
                name={"item.president"}
                checked={emp.isPresident}
                value={emp.committeeRole.id}
                disabled
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [editedActiveTo, setEditedActiveTo] = useState<Date | null>(null);
  const [employee, setEmployee] = useState<CodeBook>();
  const [filteredEmployees, setFilteredEmployees] = useState<CodeBook[]>([]);
  const [oibEmployee, setOibEmployee] = useState<OibEmployee | null>(null);
  const [showOibAlert, setShowOibAlert] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        const result = await getInstitutionCommitteeById(+id);

        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDateChange = (date: Date, field: "dateFrom" | "dateTo") => {
    const newItem = { ...item, [field]: date };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
    setEmployee(expertCommissionEmployee.employee ?? undefined);
    setOibEmployee(expertCommissionEmployee.oibEmployee);
    setEditedActiveTo(
      expertCommissionEmployee.deactivationDate
        ? new Date(expertCommissionEmployee.deactivationDate)
        : null
    );
  };
  const handleEmployeeFilterChange = async (filter: string) => {
    const search: CodeBookPagedFilter = {
      pageSize: 100,
      page: 1,
      search: filter,
    };
    const result = await searchInstitutionCommitteeEmployee(search);
    setFilteredEmployees(result.results);
  };
  const handleEmployeeChangeMultiselect = (e: any) => {
    if (!canEmployeeBeRemoved(editedActiveTo, editing)) {
      return;
    }

    const employeeList = e.target.value as CodeBook[];
    const employeeCodeBook =
      employeeList.length > 0 ? employeeList[0] : undefined;
    setEmployee(employeeCodeBook);
    if (employeeCodeBook) {
      setOibEmployee(null);
    }
  };
  const handleOibChanged = (e: any) => {
    const oib = e.target.value as string;
    const newOibEmployee: OibEmployee = {
      ...oibEmployee,
      oib: oib,
      lastName: undefined,
    };
    setOibEmployee(newOibEmployee);
  };
  const handleOibSystemClick = async () => {
    if (oibEmployee?.oib) {
      const result = await getInstitutionCommitteeEmployeeOib(oibEmployee.oib);
      if (result && result.item && result.item.oib) {
        const newOibEmployee: OibEmployee = {
          ...oibEmployee,
          firstName: result.item.firstName,
          lastName: result.item.lastName,
        };
        setOibEmployee(newOibEmployee);
        setEmployee(undefined);
        //setOibSchoolCandidate(result);
      } else {
        setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
      }
    }
  };

  const isSelectedEmployeePsychologist = useMemo(
    () =>
      tableData.some(
        (emp) =>
          emp.employee?.id === employee?.id &&
          emp.committeeRole.id === CommitteeRoleEnum.AssignedPsychologist
      ),
    [tableData, employee?.id]
  );

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.expertCommission.pageNameEdit")}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.institution")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {item.institution?.code} - {item.institution?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>
      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              maxLength={5000}
              disabled
            />
          </Col>
        </Row>
        <Row>
          <CommitteeDateRangePicker
            dateFrom={item.dateFrom ? new Date(item.dateFrom) : null}
            dateTo={item.dateTo ? new Date(item.dateTo) : null}
            onDateFromChange={(date) => handleDateChange(date, "dateFrom")}
            onDateToChange={(date) => handleDateChange(date, "dateTo")}
            errors={errors}
          />
          <Col md={6}></Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <ToolbarEdit
        showSave={"show"}
        showCancel={"show"}
        handleSave={async () => saveInstitutionCommittee(item)}
        redirectUrlSaveOK={committeeApprovalDetailUrl}
        setSaveErrors={setErrors}
        onSaveSuccess={(data: SaveResult<InstitutionCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
      />
      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation
        show={editing != null}
        onConfirm={() => setEditing(null)}
        onClose={() => setEditing(null)}
        onHide={() => setEditing(null)}
      >
        <BSModal.Header>{editing?.committeeRole!.name}</BSModal.Header>
        <BSModal.Body>
          <Container>
            <Alert
              variant="danger"
              show={showOibAlert !== null}
              onClose={() => setShowOibAlert(null)}
              dismissible
            >
              {showOibAlert}
            </Alert>
            <Row>
              <Col md={9}>
                <AutocompleteMultipleSelect
                  label={t("common.expertCommission.employee")}
                  name={"employeeId"}
                  value={employee?.id !== undefined ? [employee] : []}
                  valueProp={"id"}
                  textProp={"name"}
                  onChange={handleEmployeeChangeMultiselect}
                  selectOptions={filteredEmployees}
                  onFilterChange={handleEmployeeFilterChange}
                  maximumSelectedItems={1}
                />
              </Col>
            </Row>
            {editing?.committeeRole.checkOIB && (
              <>
                <Row>
                  <Col md={8}>
                    <TextField
                      label={t("common.schoolCandidateModel.oib")}
                      value={oibEmployee?.oib}
                      onChange={handleOibChanged}
                      disabled={employee?.id !== undefined}
                    />
                  </Col>
                  <Col md={4}>
                    <Button
                      onClick={handleOibSystemClick}
                      disabled={
                        !(
                          oibEmployee?.oib !== undefined &&
                          OIB_REGEX.test(oibEmployee.oib)
                        )
                      }
                    >
                      {t("common.schoolCandidateModel.connectOibSystem")}
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div>
                      {oibEmployee?.lastName !== undefined
                        ? `${oibEmployee?.oib}, ${oibEmployee?.lastName}, ${oibEmployee?.firstName}`
                        : ""}
                    </div>
                  </Col>
                </Row>
              </>
            )}
            {isSelectedEmployeePsychologist && (
              <DatePickerRange
                onChange={setEditedActiveTo}
                value={editedActiveTo}
                disabled={!employee || editing?.id === 0}
              />
            )}
          </Container>
        </BSModal.Body>
        <BSModal.Footer>
          <Button
            onClick={() => {
              setEmployee(undefined);
              setOibEmployee(null);
              setEditing(null);
              setFilteredEmployees([]);
            }}
            size="sm"
            outline={true}
          >
            {t("common.quit")}
          </Button>
          <Button
            onClick={() => {
              editing!.employee =
                employee && employee.id ? employee : undefined;
              editing!.oibEmployee =
                oibEmployee && oibEmployee.lastName ? oibEmployee : null;
              editing!.deactivationDate =
                FormatUtils.formatToISOString(editedActiveTo);

              setFilteredEmployees([]);
              setEmployee(undefined);
              setOibEmployee(null);
              setEditedActiveTo(null);
              dispatch(setIsEdited());
              setEditing(null);
              setItem((currItem) =>
                updateCommitteeEmployee(currItem, editing!)
              );
            }}
            size="sm"
          >
            {t("common.save")}
          </Button>
        </BSModal.Footer>
      </BSModal>
    </ContentArea>
  );
}
